import React, {useMemo} from "react";
import Select, {components} from 'react-select';
import {Box, Chip, InputLabel, styled, FormHelperText, Typography, CircularProgress} from "@material-ui/core";
import createFilterOptions from "react-select-fast-filter-options";
import FormControl from "@material-ui/core/FormControl";
import { FixedSizeList } from 'react-window';
import {DropdownIndicator} from "react-select/lib/components/indicators";

const createMultiFieldFilter = (fields = ['label', 'value']) => {
    return (option, rawInput) => {
        if (!rawInput) return true;

        const input = String(rawInput).toLowerCase().trim();

        // Check if any of the specified fields match the input
        return fields.some(field => {
            const value = option.data[field];
            return value && String(value).toLowerCase().includes(input);
        });
    };
};

// Default to label and value fields
const defaultFilterOption = createMultiFieldFilter();

const StyledSelect = styled(Select)(({ theme, $selectorStyles }) => ({
    height: "40px",
    fontSize: "16px",
    position: "relative",
    '& .enhanced-select__menu-portal': {
        zIndex: 99999,
        position: 'fixed'
    },
    ...$selectorStyles,
}));

const StyledBox = styled(Box)({
    flex: 1,
    display: "flex",
    flexDirection: "column",
});

const StyledInputLabel = styled(InputLabel)(({ theme, error, menuopen, label, labelstyles }) => ({
    alignSelf: "flex-start",
    marginBottom: label !== undefined ? "4px" : "",
    color: error ? '#f44336' : menuopen ? "black" : "rgba(0, 0, 0, 0.54)",
    "&.MuiInputLabel-formControl": {
        position: "relative",
        transform: "none",
    },
    "& .MuiInputLabel-asterisk": {
        color: "#f44336"
    },
    ...labelstyles
}));

// Memoized Option component to avoid unnecessary re-renders
const Option = React.memo(({ children, ...props }) => {
    const { onMouseMove, onMouseOver, ...rest } = props.innerProps;
    const newProps = { ...props, innerProps: rest };
    return (
        <components.Option {...newProps} className="enhanced-select-option" style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            padding: '8px 12px',
            fontSize: '14px',
            width: '100%',
            boxSizing: 'border-box',
            cursor: 'pointer',
            backgroundColor: props.isFocused ? '#f5f5f5' : 'transparent',
            color: props.isSelected ? 'white' : 'inherit',
            '&:hover': {
                backgroundColor: props.isSelected ? '#006BFF' : '#f5f5f5'
            }
        }}>
            <div style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%'
            }}>
                {children}
            </div>
        </components.Option>
    );
});

const SingleValue = ({ children, ...props }) => (
    <components.SingleValue {...props}>
        {props.data.label}
    </components.SingleValue>
);

const MultiValue = React.memo(props => {
    const { data, removeProps, chipStyle } = props;
    return (
        <Chip
            style={{
                margin: "3px",
                backgroundColor: 'white',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                maxWidth: '100%',
                ...chipStyle
            }}
            variant="outlined"
            label={data.value}
            onDelete={removeProps.onClick}
            onMouseDown={(event) => {
                event.stopPropagation();
            }}
        />
    );
});

// Add this memoized component near the top with other component definitions
const MemoizedOption = React.memo(Option, (prevProps, nextProps) => {
    return prevProps.data.value === nextProps.data.data.value
        && prevProps.isSelected === nextProps.isSelected
        && prevProps.isFocused === nextProps.isFocused;
});

// Consolidate styles
const commonStyles = {
    menuList: {
        padding: 0,
        overflowY: 'auto',
        overflowX: 'hidden'
    },
    option: {
        cursor: 'pointer',
        overflow: 'hidden',
        minHeight: '35px',
        display: 'flex',
        alignItems: 'center'
    },
    singleValue: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 'calc(100% - 20px)'
    }
};

// Simplify MenuList component
const MenuList = React.memo(props => {
    const { options, children, getValue, selectProps } = props;
    const [value] = getValue();
    const initialOffset = options.indexOf(value) * (selectProps.itemHeight || 35);
    const menuHeight = selectProps.menuHeight || 300;
    const itemHeight = selectProps.itemHeight || 35;

    return (
        <FixedSizeList
            height={Math.min(menuHeight, children.length * itemHeight)}
            itemCount={children.length}
            itemSize={itemHeight}
            width="100%"
            initialScrollOffset={initialOffset}
            overscanCount={5}
            style={{ overflowX: 'hidden' }}
        >
            {({ index, style }) => (
                <div style={{
                    ...style,
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: `${itemHeight}px`,
                    height: 'auto'
                }}>
                    {children[index]}
                </div>
            )}
        </FixedSizeList>
    );
});

const EnhancedSelect = React.forwardRef((props, ref) => {
    const [menuOpen, setMenuOpen] = React.useState(false);
    const hasError = props.error !== undefined && props.error !== "";

    const options = useMemo(() => props.options, [props.options]);

    const searchableFields = useMemo(() =>
            props.searchableFields || ['label', 'value'],
        [props.searchableFields]
    );

    const customFilterOption = useMemo(() =>
            props.searchableFields ? createMultiFieldFilter(props.searchableFields) : defaultFilterOption,
        [props.searchableFields]
    );

    const filterOptions = useMemo(() => {
        if (props.filterOptions) return props.filterOptions;

        return createFilterOptions({
            options,
            ignoreAccents: false,
            keys: searchableFields,
        });
    }, [options, props.filterOptions, searchableFields]);

    // Move getStyles inside the component
    const getStyles = useMemo(() => ({
        option: (baseStyles, state) => {
            const level = state.data?.label ? (state.data.label.split('>').length - 1) : 0;
            const itemHeight = props.itemHeight || 35;
            return {
                ...baseStyles,
                ...commonStyles.option,
                backgroundColor: state.isSelected ? "#006BFF" : state.isFocused ? "#f5f5f5" : undefined,
                paddingRight: '12px',
                fontSize: '14px',
                minHeight: `${itemHeight}px`,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'normal',
                color: state.isSelected
                    ? 'white'
                    : level > 0
                        ? 'rgba(0, 0, 0, 0.7)'
                        : 'rgba(0, 0, 0, 0.9)',
                '& > div': {
                    flex: 1,
                    minWidth: 0,
                    padding: '4px 0',
                    color: 'inherit'
                },
                '&:hover': {
                    backgroundColor: state.isSelected ? "#006BFF" : "#f5f5f5",
                    color: state.isSelected ? 'white' : undefined
                }
            };
        },
        menu: (baseStyles) => ({
            ...baseStyles,
            ...commonStyles.menu,
            zIndex: 99999,
            backgroundColor: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginTop: 0,
            marginBottom: 0,
            maxHeight: props.menuHeight || 300
        }),
        menuList: (baseStyles) => ({
            ...baseStyles,
            ...commonStyles.menuList,
            maxHeight: props.menuHeight || 300,
            padding: 0,
            '& .enhanced-select-option': {
                minHeight: `${props.itemHeight || 35}px`,
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'normal',
                wordBreak: 'break-word'
            }
        }),
        container: (baseStyles) => ({
            ...baseStyles,
            position: 'relative'
        }),
        control: (baseStyles, state) => ({
            ...baseStyles,
            // minHeight: '40px',
            // height: '40px',
            cursor: 'pointer',
            borderColor: state.isFocused
                ? state.selectProps.error ? '#f44336' : 'black'
                : state.selectProps.error ? '#f44336' : 'rgba(0, 0, 0, 0.23)',
            borderWidth: state.isFocused ? '2px' : '1px',
            boxShadow: 'none',
            backgroundColor: 'white',
            '&:hover': {
                borderColor: state.isFocused
                    ? state.selectProps.error ? '#f44336' : 'black'
                    : state.selectProps.error ? '#f44336' : 'rgba(0, 0, 0, 0.87)'
            },
            whiteSpace: "nowrap"
        }),
        dropdownIndicator: (baseStyles, state) => ({
            ...baseStyles,
            transition: 'transform 0.2s ease',
            transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : undefined
        }),
        valueContainer: (baseStyles) => ({
            ...baseStyles,
            // height: '40px',
            padding: '0 8px'
        }),
        singleValue: (baseStyles) => ({
            ...baseStyles,
            ...commonStyles.singleValue
        }),
        menuPortal: (baseStyles) => ({
            ...baseStyles,
            zIndex: 99999,
        })
    }), [props.menuHeight, props.itemHeight]);

    return (
        <StyledBox style={props.containerStyle}>
            <StyledInputLabel labelstyles={props.labelstyles} style={{ ...props.labelStyle }}
                              error={hasError} menuopen={menuOpen ? 1 : 0} label={props.label}
                              htmlFor={props.name}
                              required={props.required}
            >
                {props.label}
            </StyledInputLabel>

            {props.description && (
                <Typography style={{textAlign: "left", ...props.labelDescriptionStyles}}>
                    {props.description}
                </Typography>
            )}

            <StyledSelect
                filterOption={props.filterOption || customFilterOption}
                filterOptions={filterOptions}
                components={{
                    ...props.components,
                    MenuList,
                    MultiValue,
                    SingleValue,
                    Option: MemoizedOption,
                    DropdownIndicator: (innerProps) => props.dropdownIndicatorTemplate !== undefined
                        ? props.dropdownIndicatorTemplate
                        : props.loading
                            ? <CircularProgress style={{height: "20px", width: "20px", margin: "10px"}}/>
                            : <DropdownIndicator {...innerProps} />
                }}
                required={props.required}
                ref={ref}
                isDisabled={props.disabled}
                isMulti={props.isMulti}
                options={options}
                placeholder={props.placeholder}
                $selectorStyles={props.selectorStyle}
                onMenuOpen={() => setMenuOpen(true)}
                onMenuClose={() => setMenuOpen(false)}
                menuPortalTarget={document.body}
                menuPlacement="bottom"
                menuPosition="absolute"
                formatOptionLabel={props.formatOptionLabel && ((option) => props.formatOptionLabel(option))}
                isOptionDisabled={(option) => option.disabled}
                styles={getStyles}
                {...props}
                error={hasError}
                classNamePrefix="enhanced-select"
            />
            {props.helperText && (
                <FormHelperText style={{ margin: "3px 14px 0" }} error={hasError}>{props.helperText}</FormHelperText>
            )}
        </StyledBox>
    );
});

export const FormHelperEnhancedSelectWrapper = ({
                                                    input: {name, value, onChange, ...restInput},
                                                    meta,
                                                    label,
                                                    formControlProps,
                                                    shrink,
                                                    ...rest
                                                }) => {
    const showError = !(!(((meta.submitError && !meta.dirtySinceLastSubmit) || meta.error) && meta.touched));
    const foundValue = rest.options?.find(r => r.value === value) ?? "";

    return (
        <FormControl {...formControlProps} error={showError}>
            <EnhancedSelect
                {...rest}
                name={name}
                label={label}
                onChange={(e) => onChange(e.value)}
                inputProps={restInput}
                value={foundValue}
                error={showError ? meta.error || meta.submitError : ""}
                helperText={showError ? meta.error || meta.submitError : ""}
            />
        </FormControl>
    );
};

export default EnhancedSelect;
